import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Table, UncontrolledTooltip } from 'reactstrap';
import { Field, reduxForm } from 'redux-form';
import { useRecoilValue } from 'recoil';
import moment from 'moment';
import InformationOutlineIcon from 'mdi-react/InformationOutlineIcon';
import LoadingIcon from 'mdi-react/LoadingIcon';
import Api from '../../util/api';
import Pagination from '../../shared/components/pagination/Pagination';
import { partial } from '../../util/translation/translation';
import renderField from '../../shared/components/custom/Field';
import renderSelectField from '../../shared/components/form/Select';
import FormCard from '../../shared/components/custom/FormCard';
import SortDirection from '../../shared/components/custom/SortDirection';
import seasonsState from '../../recoil/seasons/selector';
import { formatDate } from '../../util/date-and-time';

const DATE_FORMAT = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 300;

const percentOf = (value, total) =>
  total ? Math.round((value / total) * 100) : 0;

// Seat usage below/above these marks is flagged amber/green, which points at
// companies that aren't using their seats.
const fillTone = (percent) => {
  if (percent < 25) return 'low';
  if (percent > 75) return 'high';
  return 'mid';
};

const getEligibleCompanies = (event, clubCompanies) => {
  if (!event.accessCategories?.length && !event.accessLevels?.length) {
    return clubCompanies;
  }

  return clubCompanies.filter((company) => {
    const hasMatchingGroup = event.accessCategories?.some((accessCategory) =>
      company.groups?.some((group) => group.fieldValue === accessCategory)
    );

    const hasMatchingLevel = event.accessLevels
      ?.flat()
      .some((accessLevel) => company.companyLevel === accessLevel);

    return hasMatchingGroup || hasMatchingLevel;
  });
};

// How many of a signup's party showed up. attendedAmount is what the
// participant list's attendance stepper records; status only says whether
// anyone attended, so it's the fallback for data without an amount.
const attendedFor = (participant) => {
  if (typeof participant.attendedAmount === 'number') {
    return participant.attendedAmount;
  }
  return participant.status === 'ATTENDED'
    ? (participant.friendAmount || 0) + 1
    : 0;
};

const getParticipantStats = (events, companies, search) =>
  companies
    .filter(
      (company) =>
        !search || company.name.toLowerCase().includes(search.toLowerCase())
    )
    .map((company) => {
      const stats = {
        companyName: company.name,
        companyId: company.id,
        totalFriends: 0,
        totalAvailableSpots: 0,
        totalAttended: 0,
      };

      events
        .filter((event) => getEligibleCompanies(event, [company]).length > 0)
        .forEach((event) => {
          const availableSpots = event.category
            ? event.category.maxAccountAttendees
            : event.maxAccountAttendees;
          stats.totalAvailableSpots += availableSpots || 0;

          event.participants
            .filter((participant) => participant.companyId === company.id)
            .forEach((participant) => {
              stats.totalFriends += (participant.friendAmount || 0) + 1;
              stats.totalAttended += attendedFor(participant);
            });
        });

      return stats.totalFriends === 0 &&
        stats.totalAvailableSpots === 0 &&
        stats.totalAttended === 0
        ? null
        : stats;
    })
    .filter(Boolean);

const ParticipantStatsPage = ({ change, initialize }) => {
  const s = partial('shared');
  const epp = partial('EventParticipantsPage');
  const ep = partial('EventsPage');
  const seasons = useRecoilValue(seasonsState);

  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [range, setRange] = useState({
    startDate: moment().subtract(1, 'month').format(DATE_FORMAT),
    endDate: moment().format(DATE_FORMAT),
  });
  const [category, setCategory] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [order, setOrder] = useState({ orderBy: 'companyName', desc: false });
  const [page, setPage] = useState({ currentPage: 1, perPage: 10 });

  // Everything is filtered locally, so the club's events, companies and
  // categories are fetched once rather than on every filter change.
  const load = async () => {
    setStatus('loading');
    try {
      const [resEventCategories, clubCompanies, resEvents] = await Promise.all([
        Api.events.getEventCategories(),
        Api.companies.getClubCompanies(),
        Api.events.all(),
      ]);
      setData({
        categories: resEventCategories.data,
        companies: clubCompanies,
        events: resEvents.data,
      });
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  };

  useEffect(() => {
    initialize({
      startDate: range.startDate,
      endDate: range.endDate,
      category: { label: s('all'), value: null },
    });
    load();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Any filter change starts over from the first page.
  const filterKey = `${range.startDate}|${range.endDate}|${category}|${search}|${order.orderBy}|${order.desc}`;
  useEffect(() => {
    setPage((old) => ({ ...old, currentPage: 1 }));
  }, [filterKey]);

  const rangeInvalid =
    !!range.startDate &&
    !!range.endDate &&
    moment(range.endDate).isBefore(moment(range.startDate));

  const categoryOptions = [
    { label: s('all'), value: null },
    ...(data?.categories || []).map(({ id, title }) => ({
      value: id,
      label: title,
    })),
  ];

  const result = useMemo(() => {
    if (!data || rangeInvalid) return null;
    const start = moment(`${range.startDate}T00:00:00`);
    const end = moment(`${range.endDate}T23:59:00`);
    const events = data.events.filter(
      (event) =>
        moment(event.endDate) > start &&
        moment(event.endDate) < end &&
        (!category || event.category?.id === category)
    );
    const rows = getParticipantStats(events, data.companies, search).sort(
      (a, b) => {
        const valA = a[order.orderBy];
        const valB = b[order.orderBy];
        const diff =
          typeof valA === 'string' ? valA.localeCompare(valB) : valA - valB;
        return order.desc ? -diff : diff;
      }
    );
    const totals = rows.reduce(
      (acc, row) => ({
        available: acc.available + row.totalAvailableSpots,
        signedUp: acc.signedUp + row.totalFriends,
        attended: acc.attended + row.totalAttended,
      }),
      { available: 0, signedUp: 0, attended: 0 }
    );
    return { rows, totals, eventCount: events.length };
  }, [data, range, category, search, order, rangeInvalid]);

  const pageRows = useMemo(() => {
    if (!result) return [];
    const start = (page.currentPage - 1) * page.perPage;
    return result.rows.slice(start, start + page.perPage);
  }, [result, page]);

  const setDates = (startDate, endDate) => {
    change('startDate', startDate);
    change('endDate', endDate);
    setRange({ startDate, endDate });
  };

  const currentSeason = seasons.find(
    (season) =>
      season.start_date &&
      season.end_date &&
      moment().isBetween(moment(season.start_date), moment(season.end_date))
  );
  const quickRanges = [
    {
      key: 'last30',
      label: ep('rangeLast30'),
      start: moment().subtract(30, 'days'),
      end: moment(),
    },
    {
      key: 'quarter',
      label: ep('rangeQuarter'),
      start: moment().startOf('quarter'),
      end: moment().endOf('quarter'),
    },
    ...(currentSeason
      ? [
          {
            key: 'season',
            label: ep('rangeSeason'),
            start: moment(currentSeason.start_date),
            end: moment(currentSeason.end_date),
          },
        ]
      : []),
    {
      key: 'year',
      label: ep('rangeYear'),
      start: moment().startOf('year'),
      end: moment().endOf('year'),
    },
  ].map((quick) => ({
    ...quick,
    start: quick.start.format(DATE_FORMAT),
    end: quick.end.format(DATE_FORMAT),
  }));

  const onChangePage = (pager) => {
    if (
      pager.pageSize !== page.perPage ||
      pager.currentPage !== page.currentPage
    ) {
      setPage({ currentPage: pager.currentPage, perPage: pager.pageSize });
    }
  };

  const handleSort = (column) =>
    setOrder((old) => ({
      orderBy: column,
      desc: column === old.orderBy ? !old.desc : true,
    }));

  const sortableHeader = (column, label) => (
    <th className="sortable" onClick={() => handleSort(column)}>
      {label}
      <SortDirection
        active={order.orderBy === column}
        direction={!order.desc}
      />
    </th>
  );

  const renderStatTile = (id, label, tooltip, value, total) => (
    <div className="participant-stats__item">
      <span className="participant-stats__label">
        {label}
        <InformationOutlineIcon
          id={id}
          className="participant-stats__info"
          aria-label={tooltip}
        />
        <UncontrolledTooltip placement="top" target={id}>
          {tooltip}
        </UncontrolledTooltip>
      </span>
      <span className="participant-stats__value">
        {value}
        <span className="participant-stats__total"> / {total}</span>
      </span>
      <div className="participant-stats__bar">
        <div style={{ width: `${Math.min(100, percentOf(value, total))}%` }} />
      </div>
    </div>
  );

  const renderMessageRow = (children) => (
    <tr>
      <td colSpan={4} className="text-center py-4">
        {children}
      </td>
    </tr>
  );

  const renderRows = () => {
    if (status === 'error') {
      return renderMessageRow(
        <div className="stats-message">
          <span>{ep('statsLoadError')}</span>
          <Button color="secondary" size="sm" className="mb-0" onClick={load}>
            {ep('retry')}
          </Button>
        </div>
      );
    }
    if (status === 'loading' && !data) {
      return renderMessageRow(
        <LoadingIcon className="stats-spinner" aria-hidden />
      );
    }
    if (rangeInvalid) {
      return renderMessageRow(
        <span className="text-muted">{ep('dateOrderError')}</span>
      );
    }
    if (!pageRows.length) {
      return renderMessageRow(
        <div className="stats-message text-muted">
          <span>{ep('noCompanyStats')}</span>
          <small>{ep('noCompanyStatsHint')}</small>
        </div>
      );
    }
    return pageRows.map((row) => {
      const fill = percentOf(row.totalFriends, row.totalAvailableSpots);
      const attendance = percentOf(row.totalAttended, row.totalFriends);
      return (
        <tr key={`company-${row.companyId}`}>
          <td className="table-stack__primary">
            <Link to={`/companies/edit/${row.companyId}?tab=1`}>
              {row.companyName}
            </Link>
          </td>
          <td data-label={ep('availableSeats')}>{row.totalAvailableSpots}</td>
          <td data-label={ep('signedUp')}>
            {row.totalFriends}
            {row.totalAvailableSpots > 0 && (
              <>
                <span className={`stats-fill stats-fill--${fillTone(fill)}`}>
                  {' '}
                  ({fill}%)
                </span>
                <div className={`mini-bar mini-bar--${fillTone(fill)}`}>
                  <div style={{ width: `${Math.min(100, fill)}%` }} />
                </div>
              </>
            )}
          </td>
          <td data-label={epp('attended')}>
            {row.totalAttended}
            {row.totalFriends > 0 && (
              <span className="text-muted"> ({attendance}%)</span>
            )}
          </td>
        </tr>
      );
    });
  };

  return (
    <>
      {result && (
        <div className="participant-stats">
          {renderStatTile(
            'stats-signed-up-info',
            ep('signedUp'),
            ep('signedUpTooltip'),
            result.totals.signedUp,
            result.totals.available
          )}
          {renderStatTile(
            'stats-attended-info',
            epp('attended'),
            ep('attendedTooltip'),
            result.totals.attended,
            result.totals.signedUp
          )}
        </div>
      )}
      <FormCard
        title={ep('statsTitle')}
        actions={
          status === 'loading' && data ? (
            <LoadingIcon className="stats-spinner" aria-hidden />
          ) : null
        }
        bodyClassName="form-card__body--flush"
      >
        <form
          className="form stats-filters"
          onSubmit={(e) => e.preventDefault()}
        >
          <div className="stats-filters__ranges">
            {quickRanges.map((quick) => (
              <button
                key={quick.key}
                type="button"
                className={`stats-range${
                  range.startDate === quick.start && range.endDate === quick.end
                    ? ' active'
                    : ''
                }`}
                onClick={() => setDates(quick.start, quick.end)}
              >
                {quick.label}
              </button>
            ))}
          </div>
          <div className="form__form-group">
            <span className="form-header">{s('from')}</span>
            <Field
              name="startDate"
              component={renderField}
              type="date"
              onChange={(e) =>
                setRange((prev) => ({ ...prev, startDate: e.target.value }))
              }
            />
          </div>
          <div className="form__form-group">
            <span className="form-header">{s('to')}</span>
            <Field
              name="endDate"
              component={renderField}
              type="date"
              onChange={(e) =>
                setRange((prev) => ({ ...prev, endDate: e.target.value }))
              }
            />
            {rangeInvalid && (
              <span className="stats-filters__error">
                {ep('dateOrderError')}
              </span>
            )}
          </div>
          <div className="form__form-group">
            <span className="form-header">{s('category')}</span>
            <Field
              name="category"
              placeholder={s('all')}
              options={categoryOptions}
              component={renderSelectField}
              onChange={(option) => setCategory(option?.value ?? null)}
            />
          </div>
          <div className="form__form-group">
            <span className="form-header">{s('search')}</span>
            <Field
              name="search"
              component={renderField}
              placeholder={s('companyName')}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </form>
        {result && (
          <div className="stats-summary">
            {ep('statsSummary', {
              companies: result.rows.length,
              events: result.eventCount,
              from: formatDate(range.startDate),
              to: formatDate(range.endDate),
            })}
          </div>
        )}
        <div
          className={`stats-table${
            status === 'loading' && data ? ' stats-table--busy' : ''
          }`}
        >
          <Table responsive striped className="table--roomy table--stack">
            <thead>
              <tr>
                {sortableHeader('companyName', s('companyName'))}
                {sortableHeader('totalAvailableSpots', ep('availableSeats'))}
                {sortableHeader('totalFriends', ep('signedUp'))}
                {sortableHeader('totalAttended', epp('attended'))}
              </tr>
            </thead>
            <tbody>{renderRows()}</tbody>
          </Table>
        </div>
        {!!result?.rows.length && (
          <div className="stats-filters__pagination">
            <Pagination
              key={filterKey}
              items={result.rows.length}
              onChangePage={onChangePage}
              initialPage={1}
            />
          </div>
        )}
      </FormCard>
    </>
  );
};

const reduxF = reduxForm({
  form: 'ParticipantStatsForm',
});

export default reduxF(ParticipantStatsPage);
